import { query, queryAll } from '../core/dom.js';

const SELECTORS = {
  slider: '[data-estampas-slider]',
  track: '[data-estampas-slider-track]',
  prev: '[data-estampas-slider-prev]',
  next: '[data-estampas-slider-next]',
};

const clampIndex = (value, min, max) => Math.min(Math.max(value, min), max);

const setupDrag = (slider, slidesCount, snapTo) => {
  let isDragging = false;
  let startX = 0;
  let totalDelta = 0;
  let activePointerId = null;

  const handlePointerMove = (event) => {
    if (!isDragging) {
      return;
    }

    totalDelta = event.clientX - startX;
  };

  const stopDragging = () => {
    if (!isDragging) {
      return;
    }

    isDragging = false;

    if (activePointerId !== null) {
      slider.releasePointerCapture?.(activePointerId);
      activePointerId = null;
    }

    const threshold = 35;
    if (totalDelta > threshold) {
      snapTo(-1);
    } else if (totalDelta < -threshold) {
      snapTo(1);
    }

    totalDelta = 0;
  };

  slider.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    isDragging = true;
    startX = event.clientX;
    activePointerId = event.pointerId;
    slider.setPointerCapture?.(event.pointerId);
    totalDelta = 0;
    event.preventDefault();
  });

  slider.addEventListener('pointermove', handlePointerMove);
  slider.addEventListener('pointerup', stopDragging);
  slider.addEventListener('pointercancel', stopDragging);
  slider.addEventListener('mouseleave', stopDragging);
};

const setupSlider = (slider) => {
  const track = query(SELECTORS.track, slider);

  if (!track) {
    return;
  }

  const slides = Array.from(track.children);

  if (!slides.length) {
    return;
  }

  const prevButton = query(SELECTORS.prev, slider);
  const nextButton = query(SELECTORS.next, slider);

  let activeIndex = 0;

  const updateState = () => {
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  };

  const dispatchActiveSlide = () => {
    slider.dispatchEvent(
      new CustomEvent('estampa:slide-active', {
        detail: { index: activeIndex },
        bubbles: true,
      }),
    );
  };

  const goToSlide = (index) => {
    const nextIndex = clampIndex(index, 0, slides.length - 1);

    if (nextIndex === activeIndex) {
      return;
    }

    activeIndex = nextIndex;
    updateState();
    dispatchActiveSlide();
  };

  const handleExternalSlideChange = (event) => {
    const { detail } = event;
    if (!detail || typeof detail.index !== 'number') {
      return;
    }

    goToSlide(detail.index);
  };

  slider.addEventListener('estampa:slide-select', handleExternalSlideChange);

  prevButton?.addEventListener('click', () => goToSlide(activeIndex - 1));
  nextButton?.addEventListener('click', () => goToSlide(activeIndex + 1));

  setupDrag(slider, slides.length, (direction) => goToSlide(activeIndex + direction));

  updateState();
};

export function initEstampasSlider() {
  const sliders = queryAll(SELECTORS.slider);

  if (!sliders.length) {
    return;
  }

  sliders.forEach(setupSlider);
}
